import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-white/85 focus-visible:ring-0 focus-visible:shadow-[inset_0_0_0_1px_rgba(255,255,255,.92),0_0_0_3px_rgba(255,255,255,.32)] disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-0 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-to-r from-[#3a244d] to-[#271832] text-primary-foreground shadow-[0_10px_24px_rgba(49,33,60,.16)] hover:from-[#432a58] hover:to-[#2f1d3c]",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-0 dark:bg-destructive/60 dark:focus-visible:ring-0",
        outline:
          "border border-input bg-background/80 shadow-xs hover:bg-accent/40 hover:text-accent-foreground dark:border-input dark:bg-input/25 dark:hover:bg-input/45",
        secondary:
          "bg-secondary/80 text-secondary-foreground hover:bg-secondary",
        ghost:
          "hover:bg-accent/35 hover:text-accent-foreground dark:hover:bg-accent/45",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-[42px] px-4 py-2 has-[>svg]:px-3",
        xs: "h-8 gap-1 rounded-xl px-2.5 text-xs has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3.5",
        sm: "h-9 gap-1.5 rounded-xl px-3 has-[>svg]:px-2.5",
        lg: "h-11 rounded-xl px-6 has-[>svg]:px-4",
        icon: "size-10 rounded-xl",
        "icon-xs": "size-7 rounded-xl [&_svg:not([class*='size-'])]:size-3.5",
        "icon-sm": "size-9 rounded-xl",
        "icon-lg": "size-11 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
